/**
 * DashScope Wan 2.2 Animate（角色动画 / 替换）
 *
 * POST  /api/v1/services/aigc/image2video/video-synthesis
 * GET   /api/v1/tasks/{taskId}
 */
import axios from 'axios'
import { extractApiErrorMessage } from '../../extract-api-error-message'

var POLL_INTERVAL = 3000
var POLL_TIMEOUT = 10 * 60 * 1000

var POST_PATH = '/api/v1/services/aigc/image2video/video-synthesis'

function getDashScopeWanConfig() {
  return {
    baseURL: (process.env.VUE_APP_OFFICIAL_DASHSCOPE_WAN_BASE_URL || 'https://dashscope-intl.aliyuncs.com').replace(/\/$/, ''),
    apiKey: process.env.VUE_APP_OFFICIAL_DASHSCOPE_WAN_API_KEY || ''
  }
}

function createClient(async) {
  var cfg = getDashScopeWanConfig()
  if (!cfg.baseURL) throw new Error('未配置 VUE_APP_OFFICIAL_DASHSCOPE_WAN_BASE_URL')
  if (!cfg.apiKey) throw new Error('未配置 VUE_APP_OFFICIAL_DASHSCOPE_WAN_API_KEY')

  var headers = {
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + cfg.apiKey
  }
  if (async) {
    headers['X-DashScope-Async'] = 'enable'
  }

  var instance = axios.create({
    baseURL: cfg.baseURL,
    timeout: 120000,
    headers: headers
  })

  instance.interceptors.response.use(
    function (res) {
      return res.data
    },
    function (err) {
      var respData = err.response && err.response.data
      var msg =
        extractApiErrorMessage(respData) ||
        (respData && (respData.message || respData.messge)) ||
        err.message ||
        '请求失败'
      return Promise.reject(new Error(msg))
    }
  )
  return instance
}

/**
 * @param {Object} params
 * @param {string} params.referenceImage - HTTPS 图片 URL
 * @param {string} params.referenceVideo - HTTPS 视频 URL
 * @param {string} [params.animateMode] - 'animate' | 'replace'
 * @param {string} [params.animateParamMode] - 'std' | 'pro' → wan-std | wan-pro
 * @param {boolean} [params.watermark]
 */
export function submitDashScopeWan22AnimateTask(params) {
  var client = createClient(true)
  var imageUrl = params.referenceImage ? String(params.referenceImage).trim() : ''
  var videoUrl = params.referenceVideo ? String(params.referenceVideo).trim() : ''
  if (!imageUrl || !/^https?:\/\//i.test(imageUrl)) {
    return Promise.reject(new Error('需要可访问的 HTTPS 参考图片 URL'))
  }
  if (!videoUrl || !/^https?:\/\//i.test(videoUrl)) {
    return Promise.reject(new Error('需要可访问的 HTTPS 参考视频 URL'))
  }

  var animateMode = params.animateMode === 'replace' ? 'replace' : 'animate'
  var model = animateMode === 'replace' ? 'wan2.2-animate-mix' : 'wan2.2-animate-move'
  var paramMode = params.animateParamMode === 'pro' ? 'wan-pro' : 'wan-std'

  var body = {
    model: model,
    input: {
      image_url: imageUrl,
      video_url: videoUrl,
      watermark: params.watermark === true
    },
    parameters: {
      mode: paramMode
    }
  }

  return client.post(POST_PATH, body).then(function (data) {
    var output = data && data.output ? data.output : null
    var id = output && (output.task_id || output.taskId)
    if (!id) return Promise.reject(new Error('官方接口未返回任务 task_id'))
    return { taskId: String(id) }
  })
}

var DASHSCOPE_VIDEO_OSS_HOST = 'dashscope-result-sgp.oss-ap-southeast-1.aliyuncs.com'

function proxyDashScopeVideoUrl(url) {
  try {
    var u = new URL(url)
    if (u.host === DASHSCOPE_VIDEO_OSS_HOST) {
      return '/ossDashscopeProxy' + u.pathname + u.search
    }
  } catch (e) {}
  return url
}

function downloadVideoBlob(videoUrl) {
  var fetchUrl = proxyDashScopeVideoUrl(videoUrl)
  console.log('[WanAnimate] downloading video blob', { originalUrl: videoUrl, fetchUrl: fetchUrl })
  return fetch(fetchUrl)
    .then(function (res) {
      if (!res.ok) return Promise.reject(new Error('download video failed: HTTP ' + res.status))
      return res.blob()
    })
    .then(function (blob) {
      if (!blob || !blob.size) return Promise.reject(new Error('download video failed: empty blob'))
      console.log('[WanAnimate] video blob downloaded', blob.size, 'bytes')
      return blob
    })
}

function parseTaskStatus(data) {
  var output = data && data.output ? data.output : null
  var st = output && output.task_status ? String(output.task_status).toUpperCase() : ''

  var results = output && output.results ? output.results : null
  var videoUrl = ''
  if (results && results.video_url) {
    videoUrl = String(results.video_url)
  } else if (output && output.video_url) {
    videoUrl = String(output.video_url)
  }

  var msg = ''
  if (output) {
    msg =
      (output.message != null ? String(output.message) : '') ||
      (output.messge != null ? String(output.messge) : '') ||
      (output.code != null ? String(output.code) : '') ||
      ''
  }

  if (st === 'SUCCEEDED') {
    if (!videoUrl) return { status: 'failed', url: null, error: msg || '未返回 video_url' }
    return { status: 'completed', url: videoUrl, error: null }
  }

  if (st === 'FAILED' || st === 'CANCELED') {
    return { status: 'failed', url: null, error: msg || '生成失败' }
  }

  if (st === 'PENDING' || st === 'RUNNING') {
    return { status: 'processing', url: null, error: null }
  }

  if (st === 'UNKNOWN') {
    return { status: 'failed', url: null, error: msg || '任务不存在或状态未知' }
  }

  return { status: 'processing', url: null, error: null }
}

export function pollDashScopeWan22AnimateTask(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false

  var client = createClient(false)
  var path = '/api/v1/tasks/' + encodeURIComponent(taskId)

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    client
      .get(path)
      .then(function (data) {
        if (stopped) return
        var parsed = parseTaskStatus(data)

        if (parsed.status === 'completed' && parsed.url) {
          downloadVideoBlob(parsed.url)
            .then(function (blob) {
              parsed.videoBlob = blob
              if (typeof callback === 'function') callback(null, parsed)
              stop()
            })
            .catch(function (dlErr) {
              console.warn('[WanAnimate] download blob failed, using original url', dlErr)
              if (typeof callback === 'function') callback(null, parsed)
              stop()
            })
          return
        }

        if (typeof callback === 'function') {
          callback(null, parsed)
        }
        if (parsed.status === 'failed') {
          stop()
          return
        }
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
        } else {
          pollTimer = setTimeout(poll, pollInterval)
        }
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}
